import { useMemo } from "react";
import { isSameDay } from "date-fns";
import type { CalendarEvent } from "@/features/calendar/types";
import type { ClientMemberWithUser } from "@/features/clients/types";

export interface TimeSlot {
  hour: number;
  minute: number;
}

export interface EventStyle {
  top: string;
  height: string;
}

/**
 * Shared hook for calendar view logic
 */
export function useCalendarView() {
  // Generate time slots (15min intervals, 6am to 10pm)
  const timeSlots = useMemo((): TimeSlot[] => {
    const slots: TimeSlot[] = [];
    for (let hour = 0; hour <= 23; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        slots.push({ hour, minute });
      }
    }
    return slots;
  }, []);

  /**
   * Filter events based on selected members, calendars, and services
   */
  const filterEvents = useMemo(
    () =>
      (
        events: CalendarEvent[],
        selectedMembers: string[],
        selectedServices: string[],
        members: ClientMemberWithUser[] = [],
        dateFilter?: (event: CalendarEvent) => boolean,
      ) => {
        return events.filter((event) => {
          // Apply date filter if provided
          if (dateFilter && !dateFilter(event)) {
            return false;
          }

          // Filter by service
          if (
            selectedServices.length > 0 &&
            (!event.service_id || !selectedServices.includes(event.service_id))
          ) {
            return false;
          }

          // Filter by member (check both calendars_ids and performer_ids)
          if (selectedMembers.length > 0) {
            let matchesMember = false;

            // Check if event's calendar belongs to any selected member
            const memberCalendarIds = selectedMembers.flatMap((memberId) => {
              const member = members.find((m) => m.user?.id === memberId);
              return member?.membership?.calendars_ids?.ids || [];
            });

            if (memberCalendarIds.includes(event.calendar_id)) {
              matchesMember = true;
            }

            // Check if any selected member is in performer_ids
            if (
              event.performer_ids?.some((performerId) =>
                selectedMembers.includes(performerId),
              )
            ) {
              matchesMember = true;
            }

            if (!matchesMember) {
              return false;
            }
          }

          return true;
        });
      },
    [],
  );

  /**
   * Calculate event position style for a given event
   */
  const getEventStyle = useMemo(
    () =>
      (event: CalendarEvent, dayDate?: Date): EventStyle | null => {
        const eventStart = new Date(event.start_time);
        const eventEnd = new Date(event.end_time);

        // Check if event is on the specified day
        if (dayDate && !isSameDay(eventStart, dayDate)) {
          return null;
        }

        const startHour = eventStart.getHours();
        const startMinute = eventStart.getMinutes();
        const endHour = eventEnd.getHours();
        const endMinute = eventEnd.getMinutes();

        // Calculate position (6am = 0)
        const topOffset = startHour * 4 + startMinute / 15;
        const duration =
          (endHour - startHour) * 4 + (endMinute - startMinute) / 15;

        return {
          top: `${topOffset * 0.75}rem`,
          height: `${duration * 0.75}rem`,
        };
      },
    [],
  );

  /**
   * Create slot click handler that generates start/end times
   */
  const createSlotClickHandler = useMemo(
    () =>
      (onSlotClick: (startTime: number, endTime: number) => void, day: Date) =>
      (hour: number, minute: number) => {
        const slotDate = new Date(day);
        slotDate.setHours(hour, minute, 0, 0);
        const startTime = slotDate.getTime();
        const endTime = startTime + 15 * 60 * 1000; // 15 minutes later
        onSlotClick(startTime, endTime);
      },
    [],
  );

  return {
    timeSlots,
    filterEvents,
    getEventStyle,
    createSlotClickHandler,
  };
}
